import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, User, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserAvatar } from "@/components/profile/UserAvatar";
import { heroAvatars } from "@/components/profile/HeroAvatars";
import EmailPreferences from "@/components/email/EmailPreferences";
import PushNotificationManager from "@/components/notifications/PushNotificationManager";
import NotificationCategoryPrefs from "@/components/notifications/NotificationCategoryPrefs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Seo from "@/components/seo/Seo";

const passwordSchema = z.object({
  currentPassword: z.string().min(6, "Password must be at least 6 characters"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

interface CountryState {
  id: string;
  name: string;
  code: string;
}

const Settings = () => {
  const { showAds, toggleAds } = useSettings();
  const { user, supabase } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [countries, setCountries] = useState<CountryState[]>([]);
  const [states, setStates] = useState<CountryState[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);

  const form = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    if (user) {
      fetchUserAvatar();
      fetchCountries();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    if (!user) return;
    
    try {
      setIsLoadingLocation(true);
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          countries:country_id (id, name, code),
          states:state_id (id, name, code)
        `)
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error("Error fetching profile:", error);
        return;
      }
      
      if (data) {
        setUserProfile(data);
        
        // Set the selected country, default to Canada if not set
        if (data.countries) {
          setSelectedCountry(data.countries.id);
          await fetchStates(data.countries.id);
        } else {
          // Set default to Canada
          const canadaCountry = countries.find(country => country.code === 'CA');
          if (canadaCountry) {
            setSelectedCountry(canadaCountry.id);
            await fetchStates(canadaCountry.id);
          }
        }
        
        // Set the selected state, default to Ontario if not set
        if (data.states) {
          setSelectedState(data.states.id);
        } else if (data.countries?.code === 'CA' || !data.countries) {
          // Set default to Ontario if Canada or no country selected
          const ontarioState = states.find(state => state.code === 'ON');
          if (ontarioState) {
            setSelectedState(ontarioState.id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  const fetchUserAvatar = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('avatar')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error("Error fetching avatar:", error);
        return;
      }
      
      if (data && data.avatar) {
        setSelectedAvatar(data.avatar);
      }
    } catch (error) {
      console.error("Error fetching user avatar:", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .order('name');
        
      if (error) {
        throw error;
      }
      
      if (data) {
        setCountries(data);
        // After countries are loaded, fetch user profile
        await fetchUserProfile();
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Failed to load countries");
    }
  };
  
  const fetchStates = async (countryId: string) => {
    try {
      const { data, error } = await supabase
        .from('states')
        .select('*')
        .eq('country_id', countryId)
        .order('name');
        
      if (error) {
        throw error;
      }
      
      if (data) {
        setStates(data);
        
        // If no state is selected and this is Canada, default to Ontario
        if (!selectedState && countries.find(c => c.id === countryId)?.code === 'CA') {
          const ontarioState = data.find(state => state.code === 'ON');
          if (ontarioState) {
            setSelectedState(ontarioState.id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to load states/provinces");
    }
  };

  const handleCountryChange = async (countryId: string) => {
    setSelectedCountry(countryId);
    setSelectedState(null); // Reset state when country changes
    await fetchStates(countryId);
  };

  const handleStateChange = (stateId: string) => {
    setSelectedState(stateId);
  };

  const updateUserLocation = async () => {
    if (!user || !selectedCountry || !selectedState) return;
    
    try {
      setIsUpdatingLocation(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          country_id: selectedCountry,
          state_id: selectedState 
        })
        .eq('id', user.id);
        
      if (error) throw error;
      
      toast.success("Location updated successfully");
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error("Failed to update location");
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  const handleAvatarSelect = async (avatarId: string) => {
    if (!user) return;
    
    try {
      setIsUpdatingAvatar(true);
      setSelectedAvatar(avatarId);
      
      const { error } = await supabase
        .from('profiles')
        .update({ avatar: avatarId })
        .eq('id', user.id);
        
      if (error) throw error;
      
      toast.success("Avatar updated successfully");
    } catch (error) {
      console.error("Error updating avatar:", error);
      toast.error("Failed to update avatar");
    } finally {
      setIsUpdatingAvatar(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof passwordSchema>) => {
    setIsUpdating(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword,
      });
      
      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Password updated successfully");
        form.reset();
      }
    } catch (error) {
      toast.error("Failed to update password. Please try again.");
      console.error("Password update error:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Seo title="Settings" description="Manage your Grade Test Pro account settings." canonicalPath="/settings" noindex />
      <Header />
      
      <main className="flex-1 py-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Settings</h1>
            
            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="profile" className="flex items-center gap-2">
                  <User size={16} />
                  Profile
                </TabsTrigger>
                <TabsTrigger value="security" className="flex items-center gap-2">
                  <Lock size={16} />
                  Security
                </TabsTrigger>
                <TabsTrigger value="location" className="flex items-center gap-2">
                  <MapPin size={16} />
                  Location
                </TabsTrigger>
                <TabsTrigger value="notifications" className="flex items-center gap-2">
                  <Mail size={16} />
                  Notifications
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="profile">
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User />
                      Profile Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium mb-4">Choose Your Avatar</h3>
                        <div className="flex items-center space-x-4 mb-6">
                          <div className="flex-shrink-0">
                            <UserAvatar userId={user?.id} size="lg" editable={true} />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Your current avatar</p>
                            <p className="text-sm text-muted-foreground">Click on the avatar to change it</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          {heroAvatars.map((hero) => (
                            <div
                              key={hero.id}
                              className={`p-4 rounded-lg flex flex-col items-center cursor-pointer transition-all ${
                                selectedAvatar === hero.id 
                                  ? "bg-primary/10 ring-2 ring-primary" 
                                  : "hover:bg-accent"
                              }`}
                              onClick={() => handleAvatarSelect(hero.id)}
                            >
                              <div className="h-16 w-16 mb-2">
                                {hero.svg}
                              </div>
                              <span className="text-sm text-center">{hero.name}</span>
                              <span className="text-xs text-muted-foreground mt-1">
                                {hero.gender === "neutral" ? "For everyone" : 
                                  hero.gender === "male" ? "Boy hero" : "Girl hero"}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {isUpdatingAvatar && (
                          <p className="text-sm text-center mt-4 text-muted-foreground">
                            Updating your avatar...
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="security">
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lock />
                      Security Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                          control={form.control}
                          name="currentPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Current Password</FormLabel>
                              <FormControl>
                                <Input type="password" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="newPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>New Password</FormLabel>
                              <FormControl>
                                <Input type="password" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="confirmPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Confirm New Password</FormLabel>
                              <FormControl>
                                <Input type="password" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <Button 
                          type="submit" 
                          className="mt-4" 
                          disabled={isUpdating}
                        >
                          {isUpdating ? "Updating..." : "Update Password"}
                        </Button>
                      </form>
                    </Form>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="location">
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin />
                      Location Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div>
                        <p className="text-sm text-muted-foreground mb-4">
                          Your location helps us show questions and subjects relevant to your educational system.
                        </p>
                        
                        {isLoadingLocation ? (
                          <p className="text-sm text-muted-foreground">Loading location data...</p>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                              <div className="space-y-2">
                                <Label htmlFor="country">Country</Label>
                                <Select 
                                  value={selectedCountry || ''} 
                                  onValueChange={handleCountryChange}
                                >
                                  <SelectTrigger id="country">
                                    <SelectValue placeholder="Select country" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {countries.map((country) => (
                                      <SelectItem key={country.id} value={country.id}>
                                        {country.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="space-y-2">
                                <Label htmlFor="state">State/Province</Label>
                                <Select 
                                  value={selectedState || ''} 
                                  onValueChange={handleStateChange}
                                  disabled={!selectedCountry}
                                >
                                  <SelectTrigger id="state">
                                    <SelectValue placeholder="Select state/province" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {states.map((state) => (
                                      <SelectItem key={state.id} value={state.id}>
                                        {state.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <Button 
                              onClick={updateUserLocation} 
                              disabled={isUpdatingLocation || !selectedCountry || !selectedState}
                              className="w-full md:w-auto"
                            >
                              {isUpdatingLocation ? "Updating..." : "Save Location"}
                            </Button>
                          </>
                        )}
                        
                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                          <p className="text-sm text-yellow-700">
                            <strong>Note:</strong> Updating your location will affect which questions and tests are shown to you. 
                            The system will prioritize content specific to your location.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="notifications" className="space-y-6">
                <PushNotificationManager />
                <NotificationCategoryPrefs />
                <EmailPreferences />
              </TabsContent>
            </Tabs>
            
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Settings;
